import React, { useEffect, useMemo, useState } from 'react';
import { useOrganizationPlugins, useEnablePlugin, useDisablePlugin } from '@/hooks/usePlugins';
import { useOrganizations } from '@/hooks/useOrganizations';
import useAuthStore from '@/stores/authStore';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { ScrollArea } from './ui/scroll-area';
import {
  Puzzle,
  CheckCircle2,
  XCircle,
  Loader2,
  AlertTriangle,
  Building2,
  ShieldCheck,
  Zap,
} from 'lucide-react';
import type { OrganizationPlugin } from '@/services/plugins.service';
import { cn } from "@/lib/utils";
import { formatDate } from '@/lib/format';
import { useTranslation } from 'react-i18next';

const pluginMeta: Record<string, { emoji: string; title?: string; accent: string; icon?: React.ReactNode }> = {
  powerfulform: { 
    emoji: '📝', 
    accent: 'bg-orange-50 text-orange-700 border-orange-100',
    icon: <div className="h-10 w-10 rounded-lg bg-orange-100 flex items-center justify-center text-orange-600"><Zap className="h-5 w-5" /></div>
  },
  jotform: { 
    emoji: '📋', 
    accent: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    icon: <div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-600"><Puzzle className="h-5 w-5" /></div>
  },
  vynform: { 
    emoji: '🚀', 
    accent: 'bg-violet-50 text-violet-700 border-violet-100',
    icon: <div className="h-10 w-10 rounded-lg bg-violet-100 flex items-center justify-center text-violet-600"><Puzzle className="h-5 w-5" /></div>
  },
};

const getPluginMeta = (plugin: OrganizationPlugin) => {
  return pluginMeta[plugin.plugin_key] ?? {
    emoji: '🔌',
    accent: 'bg-slate-50 text-slate-700 border-slate-100',
    icon: <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600"><Puzzle className="h-5 w-5" /></div>
  };
};

const PluginsTab: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const { data: organizationsData, isLoading: orgsLoading } = useOrganizations();
  const organizations = organizationsData?.data && Array.isArray(organizationsData.data)
    ? organizationsData.data
    : [];

  const [selectedOrgId, setSelectedOrgId] = useState<string>('');

  useEffect(() => {
    if (organizations.length > 0 && !selectedOrgId) {
      setSelectedOrgId(user?.organization_id || organizations[0].id);
    }
  }, [organizations, user?.organization_id, selectedOrgId]);

  const { data: pluginResponse, isLoading, error } = useOrganizationPlugins(selectedOrgId);
  const enableMutation = useEnablePlugin(selectedOrgId || '');
  const disableMutation = useDisablePlugin(selectedOrgId || '');

  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    plugin: OrganizationPlugin | null;
    action: 'enable' | 'disable';
  }>({
    isOpen: false,
    plugin: null,
    action: 'enable',
  });

  const plugins = pluginResponse?.data?.plugins || [];
  const activePlugins = plugins.filter((plugin) => plugin.is_enabled).length;

  const sortedPlugins = useMemo(() => {
    const enabled = plugins.filter((plugin) => plugin.is_enabled);
    const disabled = plugins.filter((plugin) => !plugin.is_enabled);
    return [...enabled, ...disabled];
  }, [plugins]);

  const handleTogglePlugin = (plugin: OrganizationPlugin) => {
    setConfirmDialog({
      isOpen: true,
      plugin,
      action: plugin.is_enabled ? 'disable' : 'enable',
    });
  };

  const getDefaultConfig = (pluginKey: string): any => {
    switch (pluginKey) {
      case 'powerfulform':
        return {
          sender_email: 'no-reply@powerfulform.com',
          default_priority: 'normal',
          auto_assign: false,
        };
      case 'vynform':
        return {
          sender_email: 'form@vyndesk.com',
          default_priority: 'normal',
          auto_assign: false,
        };
      default:
        return {};
    }
  };

  const handleConfirmToggle = async () => {
    if (!confirmDialog.plugin) return;

    const { plugin, action } = confirmDialog;

    try {
      if (action === 'enable') {
        await enableMutation.mutateAsync({
          pluginId: plugin.id,
          config: getDefaultConfig(plugin.plugin_key),
        });
      } else {
        await disableMutation.mutateAsync({
          pluginId: plugin.id,
        });
      }
    } catch (toggleError) {
      console.error('Failed to toggle plugin:', toggleError);
    } finally {
      setConfirmDialog({ isOpen: false, plugin: null, action: 'enable' });
    }
  };

  if (!selectedOrgId) {
    return (
      <div className="flex items-center justify-center py-24 text-sm text-slate-500">
        {t('common:x4.noOrgSelected2', 'Keine Organization ausgewählt.')}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-10 w-64 rounded-lg" />
          <Skeleton className="h-10 w-40 rounded-lg" />
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-48 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="w-full max-w-md border-red-200 bg-red-50/70">
          <CardHeader className="gap-2 text-center">
            <AlertTriangle className="mx-auto h-8 w-8 text-red-500" />
            <h3 className="text-base font-semibold text-red-600">{t('common:x4.pluginsLoadFailed', 'Plugins konnten nicht geladen werden')}</h3>
            <p className="text-sm text-red-500">
              Bitte überprüfen Sie Ihre Verbindung oder versuchen Sie es später erneut.
            </p>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="border border-white/70 bg-white/70 backdrop-blur-xl ring-1 ring-black/5">
        <CardHeader className="flex flex-row items-center justify-between pb-2 border-b border-slate-100">
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-base font-semibold text-slate-900">{t('common:x4.pluginsIntegrations', 'Plugins & Integrationen')}</h3>
              <span className="text-xs text-slate-400 font-normal ml-2">
                ({activePlugins} von {plugins.length} aktiv)
              </span>
            </div>
            <p className="text-sm text-slate-500">
              Verwalten Sie Integrationen von Drittanbietern und erweitern Sie Ihre Arbeitsabläufe.
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-slate-500">{t('common:x1.orgColon', 'Organisation:')}</span>
              {orgsLoading ? (
                <Skeleton className="h-8 w-40 rounded-lg" />
              ) : (
                <Select value={selectedOrgId} onValueChange={setSelectedOrgId}>
                  <SelectTrigger className="h-8 w-[180px] border-slate-200 bg-slate-50/50 text-xs focus:ring-slate-200">
                    <SelectValue placeholder={t('ticket:ph.selectOrg', 'Organisation wählen')} />
                  </SelectTrigger>
                  <SelectContent>
                    {organizations.map((org) => (
                      <SelectItem key={org.id} value={org.id} className="text-xs">
                        {org.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-6">
          {plugins.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-slate-200 bg-slate-50/50 px-6 py-16 text-center text-slate-500">
              <Puzzle className="h-8 w-8 text-slate-300" />
              <p className="text-sm font-medium text-slate-600">
                {t('common:x4.noPluginsAvailable', 'Keine Plugins verfügbar')}
              </p>
              <p className="text-xs text-slate-400">
                {t('common:x4.noIntegrationsForOrg', 'Für diese Organisation sind aktuell keine Integrationen verfügbar.')}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sortedPlugins.map((plugin) => {
                const meta = getPluginMeta(plugin);
                const isProcessing = enableMutation.isPending || disableMutation.isPending;
                return (
                  <div
                    key={plugin.id}
                    className={cn(
                      "group relative flex flex-col justify-between rounded-xl border p-5 transition-all duration-200",
                      plugin.is_enabled 
                        ? "bg-white border-slate-200 hover:border-slate-300"
                        : "bg-slate-50/50 border-slate-100 hover:border-slate-200 hover:bg-white"
                    )}
                  >
                    <div className="flex justify-between items-start mb-4">
                      {meta.icon}
                      <Switch
                        checked={plugin.is_enabled || false}
                        onCheckedChange={() => handleTogglePlugin(plugin)}
                        disabled={isProcessing}
                        className="data-[state=checked]:bg-[#0F99FD]"
                      />
                    </div>
                    
                    <div className="space-y-1 mb-4">
                      <h4 className="font-semibold text-slate-900 text-sm">{plugin.plugin_name}</h4>
                      <p className="text-xs text-slate-500 line-clamp-2 min-h-[32px]">{plugin.description}</p>
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-slate-100 mt-auto">
                      <Badge
                        variant="outline"
                        className={cn(
                          "rounded-md px-2 py-0.5 text-[10px] font-medium border-0",
                          plugin.is_enabled 
                            ? "bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-600/20" 
                            : "bg-slate-100 text-slate-500 ring-1 ring-inset ring-slate-500/10"
                        )}
                      >
                        {plugin.is_enabled ? 'Aktiviert' : 'Inaktiv'}
                      </Badge>
                      
                      {plugin.is_enabled && plugin.enabled_at && (
                        <span className="text-[10px] text-slate-400">
                          {formatDate(plugin.enabled_at)}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog
        open={confirmDialog.isOpen}
        onOpenChange={(open) => setConfirmDialog((prev) => ({ ...prev, isOpen: open }))}
      >
        <AlertDialogContent className="max-w-md rounded-xl border border-white/70 bg-white/60 backdrop-blur-md ring-1 ring-black/5">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-base font-semibold text-slate-900">
              {confirmDialog.action === 'enable' ? (
                <CheckCircle2 className="h-5 w-5 text-emerald-500" />
              ) : (
                <AlertTriangle className="h-5 w-5 text-amber-500" />
              )}
              {confirmDialog.action === 'enable' ? 'Plugin aktivieren?' : 'Plugin deaktivieren?'}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-slate-500 mt-2">
              {confirmDialog.action === 'enable' ? (
                <>
                  {t('common:x1.thePlugin', 'Das Plugin')} <strong className="text-slate-700">{confirmDialog.plugin?.plugin_name}</strong> wird aktiviert und die Funktionen stehen Ihrem Team sofort zur Verfügung.
                </>
              ) : (
                <>
                  {t('common:x1.thePlugin', 'Das Plugin')} <strong className="text-slate-700">{confirmDialog.plugin?.plugin_name}</strong> wird deaktiviert. Alle damit verbundenen Automatisierungen werden gestoppt.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-4 gap-2">
            <AlertDialogCancel className="h-9 rounded-lg border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-slate-50">
              {t('common:action.cancel', 'Abbrechen')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmToggle}
              disabled={enableMutation.isPending || disableMutation.isPending}
              className={cn(
                "h-9 rounded-lg text-white transition-all",
                confirmDialog.action === 'enable'
                  ? "bg-emerald-600 hover:bg-emerald-700"
                  : "bg-red-600 hover:bg-red-700"
              )}
            >
              {(enableMutation.isPending || disableMutation.isPending) && (
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              )}
              {confirmDialog.action === 'enable' ? 'Jetzt aktivieren' : 'Jetzt deaktivieren'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PluginsTab;
